import { mat4 } from 'gl-matrix';
/**
 * Gets the current date in DICOM format (YYYYMMDD).
 * @returns A string representing the current date in DICOM format.
 */
export function getDICOMDate(date = new Date()): string {
    const year = date.getFullYear().toString();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return year + month + day;
}

/**
 * Gets the time in DICOM format (HHMMSS).
 *
 * Pass the same `date` used for {@link getDICOMDate} when stamping a pair: two
 * separate clock reads can fall either side of midnight and land a day apart.
 *
 * @returns A string representing the given time in DICOM format.
 */
export function getDICOMTime(date = new Date()): string {
    // get time in DICOM format: HHMMSS -> 225739
    const h = date.getHours();
    const m = date.getMinutes();
    const s = date.getSeconds();

    const h_str = h < 10 ? '0' + h.toString() : h.toString();
    const m_str = m < 10 ? '0' + m.toString() : m.toString();
    const s_str = s < 10 ? '0' + s.toString() : s.toString();
    return h_str + m_str + s_str;
}

/**
 * Formats a resolved DICOM DateTime string (YYYYMMDDHHMMSS.FFFFFF) into
 * a human-readable time (HH:MM:SS).
 */
export function formatDicomTime(dicomDateTime: string): string {
    if (!dicomDateTime || dicomDateTime.length < 14) {
        return dicomDateTime ?? '—';
    }
    const hh = dicomDateTime.substring(8, 10);
    const mm = dicomDateTime.substring(10, 12);
    const ss = dicomDateTime.substring(12, 14);
    return `${hh}:${mm}:${ss}`;
}

/**
 * Parses a DICOM DateTime string (YYYYMMDDHHMMSS.FFFFFF) to an absolute
 * timestamp in seconds (UTC). Unlike {@link parseDicomTime}, this preserves
 * the date component, so elapsed-time calculations work across midnight.
 */
export function parseDicomDateTime(dateTimeStr: string): number {
    const str = dateTimeStr.trim();

    const year = parseInt(str.substring(0, 4), 10);
    const month = parseInt(str.substring(4, 6), 10) - 1; // JS months are 0-indexed
    const day = parseInt(str.substring(6, 8), 10);
    const hours = parseInt(str.substring(8, 10), 10) || 0;
    const minutes = parseInt(str.substring(10, 12), 10) || 0;
    const seconds = parseFloat(str.substring(12)) || 0;

    const wholeSec = Math.floor(seconds);
    const fracSec = seconds - wholeSec;

    const date = new Date(Date.UTC(year, month, day, hours, minutes, wholeSec));
    return date.getTime() / 1000 + fracSec;
}

export function parseDicomTime(timeStr: string): number {
    // DICOM time format: HHMMSS.FFFFFF or HHMMSS
    // DICOM datetime format: YYYYMMDDHHMMSS.FFFFFF
    const str = timeStr.trim();
    let timePart = str;

    // If it's a datetime (>= 14 chars with date prefix), extract time portion
    if (str.length >= 14 && !str.includes(':')) {
        timePart = str.substring(8);
    }

    const hours = parseInt(timePart.substring(0, 2), 10) || 0;
    const minutes = parseInt(timePart.substring(2, 4), 10) || 0;
    const seconds = parseFloat(timePart.substring(4)) || 0;

    return hours * 3600 + minutes * 60 + seconds;
}

export function getImageAge(seriesDate: Date | null): string {
    if (!seriesDate) {
        return 'no date';
    }

    const now = new Date();
    const diff = now.getTime() - seriesDate.getTime();

    // Convert milliseconds to different units
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30.44); // Average month length
    const years = Math.floor(days / 365.25); // Account for leap years

    // Return the most appropriate unit
    if (minutes < 60) {
        return `${minutes} minutes`;
    }
    if (hours < 24) {
        return `${hours} hours`;
    }
    if (days < 7) {
        return `${days} days`;
    }
    if (weeks < 4) {
        return `${weeks} weeks`;
    }
    if (months < 12) {
        return `${months} months`;
    }
    if (years > 0) {
        return `${years} ${years === 1 ? 'year' : 'years'}`;
    }
    return 'no date';
}

/**
 * Formats a DICOM DateTime string (YYYYMMDDHHMMSS) into a
 * human-readable date (YYYY-MM-DD) or date-time (YYYY-MM-DD HH:MM:SS).
 * Returns '—' for missing or too-short input.
 */
export function formatDicomDateTime(dt: string | undefined): string {
    if (!dt || dt.length < 8) {
        return '—';
    }
    const y = dt.slice(0, 4);
    const m = dt.slice(4, 6);
    const d = dt.slice(6, 8);
    if (dt.length >= 14) {
        const hh = dt.slice(8, 10);
        const mm = dt.slice(10, 12);
        const ss = dt.slice(12, 14);
        return `${y}-${m}-${d} ${hh}:${mm}:${ss}`;
    }
    return `${y}-${m}-${d}`;
}

/**
 * Converts a gl-matrix mat4 (column-major) to a DICOM row-major
 * 16-element array for FrameOfReferenceTransformationMatrix.
 */
export function mat4ToRowMajorArray(m: ArrayLike<number>): number[] {
    return [
        m[0],
        m[4],
        m[8],
        m[12],
        m[1],
        m[5],
        m[9],
        m[13],
        m[2],
        m[6],
        m[10],
        m[14],
        m[3],
        m[7],
        m[11],
        m[15],
    ];
}

export function rowMajorArrayToMat4(values: ArrayLike<number>): mat4 {
    const columnMajor = mat4ToRowMajorArray(values);
    const out = mat4.create();
    for (let i = 0; i < 16; i++) {
        out[i] = columnMajor[i];
    }
    return out;
}

export function epsilonEqual(a: number, b: number, epsilon = 1e-6): boolean {
    return Math.abs(a - b) < epsilon;
}

export function roundToDecimals(a: number, decimals: number): number {
    const factor = Math.pow(10, decimals);
    return Math.round((a + Number.EPSILON) * factor) / factor;
}

export function extractRGBNumbers(rgbString: string): [number, number, number] {
    const regex = /rgb\((\d+),\s*(\d+),\s*(\d+)\)/;
    const matches = rgbString.match(regex);

    if (!matches) {
        throw new Error('Invalid RGB string format');
    }

    return matches.slice(1, 4).map(Number) as [number, number, number]; // Convert the matched strings to numbers and return as a tuple
}
